import { lift, setBlockType, toggleMark, wrapIn } from 'prosemirror-commands';
import type { MarkType, Node as ProseNode } from 'prosemirror-model';
import { liftListItem, sinkListItem, wrapInList } from 'prosemirror-schema-list';
import type { Command, EditorState } from 'prosemirror-state';
import { LINK_SCHEMES, entrySchema } from './schema.ts';

/** The toolbar's commands (docs/ENTRY-EDITOR-PLAN.md section 4.5), and what it shows as active. */

const { marks, nodes } = entrySchema;

export const toggleBold = toggleMark(marks['bold']!);
export const toggleItalic = toggleMark(marks['italic']!);
export const toggleUnderline = toggleMark(marks['underline']!);
export const toggleStrike = toggleMark(marks['strike']!);

export type BlockStyle = 'paragraph' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/** Paragraph or heading, keeping the block's alignment. */
export function setBlockStyle(style: BlockStyle): Command {
  return (state, dispatch) => {
    const align = (state.selection.$from.parent.attrs['align'] as string | null | undefined) ?? null;
    return style === 'paragraph'
      ? setBlockType(nodes['paragraph']!, { align })(state, dispatch)
      : setBlockType(nodes['heading']!, { level: Number(style.slice(1)), align })(state, dispatch);
  };
}

export function blockStyleOf(state: EditorState): BlockStyle | null {
  const { $from, $to } = state.selection;
  if (!$from.sameParent($to)) return null;
  const parent = $from.parent;
  if (parent.type === nodes['heading']) return `h${parent.attrs['level'] as number}` as BlockStyle;
  return parent.type === nodes['paragraph'] ? 'paragraph' : null;
}

export const bulletList = wrapInList(nodes['bullet_list']!);
export const numberedList = wrapInList(nodes['ordered_list']!);

/** Indent: one level deeper in a list, or else into a block quote, as WebKit's indent does. */
export const indent: Command = (state, dispatch) =>
  sinkListItem(nodes['list_item']!)(state, dispatch) || wrapIn(nodes['blockquote']!)(state, dispatch);

/** Outdent: out of a list level, or out of a block quote. */
export const outdent: Command = (state, dispatch) => liftListItem(nodes['list_item']!)(state, dispatch) || lift(state, dispatch);

export type Alignment = 'left' | 'center' | 'right' | 'justify';

/** Aligns every paragraph and heading the selection touches; left is the default, so it clears. */
export function align(alignment: Alignment): Command {
  return (state, dispatch) => {
    const { from, to } = state.selection;
    const blocks: { node: ProseNode; pos: number }[] = [];
    state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.isTextblock && 'align' in node.attrs) blocks.push({ node, pos });
      return !node.isTextblock;
    });
    if (blocks.length === 0) return false;
    if (dispatch) {
      const tr = state.tr;
      const value = alignment === 'left' ? null : alignment;
      for (const { node, pos } of blocks) tr.setNodeMarkup(pos, undefined, { ...node.attrs, align: value });
      dispatch(tr.scrollIntoView());
    }
    return true;
  };
}

export function alignmentOf(state: EditorState): Alignment {
  return ((state.selection.$from.parent.attrs['align'] as Alignment | null | undefined) ?? 'left');
}

/** Whether a mark applies at the cursor, or across the whole selection. */
export function markActive(state: EditorState, type: MarkType): boolean {
  const { from, $from, to, empty } = state.selection;
  if (empty) return !!type.isInSet(state.storedMarks ?? $from.marks());
  return state.doc.rangeHasMark(from, to, type);
}

export function isBold(state: EditorState): boolean {
  return markActive(state, marks['bold']!);
}
export function isItalic(state: EditorState): boolean {
  return markActive(state, marks['italic']!);
}
export function isUnderline(state: EditorState): boolean {
  return markActive(state, marks['underline']!);
}
export function isStrike(state: EditorState): boolean {
  return markActive(state, marks['strike']!);
}
export function inLink(state: EditorState): boolean {
  return markActive(state, marks['link']!);
}

/** Whether an address may become a link: the schemes the schema and the sanitizer keep. */
export function linkable(href: string): boolean {
  return LINK_SCHEMES.test(href.trim());
}

/** Makes the selected text a link to `href`; refuses an address the schema would drop. */
export function setLink(href: string): Command {
  return (state, dispatch) => {
    const target = href.trim();
    if (state.selection.empty || !linkable(target)) return false;
    if (dispatch) {
      const { from, to } = state.selection;
      dispatch(state.tr.removeMark(from, to, marks['link']!).addMark(from, to, marks['link']!.create({ href: target })).scrollIntoView());
    }
    return true;
  };
}

/** Removes the link around the cursor, or from the selection. */
export const removeLink: Command = (state, dispatch) => {
  const type = marks['link']!;
  const { $from, from, to, empty } = state.selection;
  let start = from;
  let end = to;
  if (empty) {
    const link = type.isInSet($from.marks());
    if (!link) return false;
    // Widen to the whole link around the cursor: the run of the block's children that carry it.
    let pos = $from.start();
    let runStart = -1;
    let runEnd = -1;
    for (let i = 0; i < $from.parent.childCount; i++) {
      const child = $from.parent.child(i);
      if (link.isInSet(child.marks)) {
        if (runStart < 0) runStart = pos;
        runEnd = pos + child.nodeSize;
      } else if (runStart >= 0) {
        if (runStart <= $from.pos && $from.pos <= runEnd) break;
        runStart = -1;
      }
      pos += child.nodeSize;
    }
    if (runStart < 0) return false;
    start = runStart;
    end = runEnd;
  }
  if (dispatch) dispatch(state.tr.removeMark(start, end, type));
  return true;
};

/** Removes every mark from the selection: bold, colours, fonts and links alike. */
export const clearFormatting: Command = (state, dispatch) => {
  const { from, to, empty } = state.selection;
  if (empty) return false;
  if (dispatch) dispatch(state.tr.removeMark(from, to).setStoredMarks([]));
  return true;
};

// --- Font, size and colour (docs/ENTRY-EDITOR-SLICE-3-PLAN.md section 3.5) --------------

/** The marks that carry one value each, and that value's attribute. */
export type ValueMark = 'font_family' | 'font_size' | 'text_color' | 'background_color';
const VALUE_ATTR: Record<ValueMark, string> = { font_family: 'family', font_size: 'size', text_color: 'color', background_color: 'color' };

/** What the selection has: one value, none (`null`), or several, or some text with and some without (`'mixed'`). */
export function markValue(state: EditorState, mark: ValueMark): string | null | 'mixed' {
  const type = marks[mark]!;
  const attr = VALUE_ATTR[mark];
  const { from, to, empty, $from } = state.selection;
  if (empty) {
    const found = type.isInSet(state.storedMarks ?? $from.marks());
    return found ? (found.attrs[attr] as string) : null;
  }
  const values = new Set<string | null>();
  state.doc.nodesBetween(from, to, (node) => {
    if (!node.isText) return;
    const found = type.isInSet(node.marks);
    values.add(found ? (found.attrs[attr] as string) : null);
  });
  if (values.size === 0) return null;
  return values.size === 1 ? [...values][0]! : 'mixed';
}

/** Gives the selection (or what's typed next) `value`, or takes the mark away for `null`. */
export function setMarkValue(mark: ValueMark, value: string | null): Command {
  const type = marks[mark]!;
  const created = value === null ? null : type.create({ [VALUE_ATTR[mark]]: value });
  return (state, dispatch) => {
    const { from, to, empty } = state.selection;
    if (dispatch) {
      const tr = state.tr;
      if (empty) {
        tr.removeStoredMark(type);
        if (created) tr.addStoredMark(created);
      } else {
        tr.removeMark(from, to, type);
        if (created) tr.addMark(from, to, created);
      }
      dispatch(tr.scrollIntoView());
    }
    return true;
  };
}

/** Every value of `mark` in the document, such as fonts the desktop wrote, so the toolbar can offer them. */
export function valuesInDoc(doc: ProseNode, mark: ValueMark): string[] {
  const type = marks[mark]!;
  const found = new Set<string>();
  doc.descendants((node) => {
    const value = type.isInSet(node.marks)?.attrs[VALUE_ATTR[mark]];
    if (typeof value === 'string') found.add(value);
  });
  return [...found];
}

/** The desktop's HTMLEditor sizes: its labels, and the keywords WebKit writes for them. 12 pt is the default, with no mark. */
export const FONT_SIZES: readonly (readonly [string | null, string])[] = [
  ['x-small', '8 pt'],
  ['small', '10 pt'],
  [null, '12 pt'],
  ['large', '14 pt'],
  ['x-large', '18 pt'],
  ['xx-large', '24 pt'],
  ['xxx-large', '36 pt'],
];

/** Common fonts, each with fallbacks and a generic family, so text looks close on a computer without the first one. */
export const FONT_FAMILIES: readonly (readonly [string, string])[] = [
  ['Georgia', 'Georgia, serif'],
  ['Times New Roman', '"Times New Roman", Times, serif'],
  ['Palatino', '"Palatino Linotype", Palatino, "Book Antiqua", serif'],
  ['Garamond', 'Garamond, "EB Garamond", serif'],
  ['Cambria', 'Cambria, Georgia, serif'],
  ['Arial', 'Arial, Helvetica, sans-serif'],
  ['Verdana', 'Verdana, Geneva, sans-serif'],
  ['Tahoma', 'Tahoma, Verdana, sans-serif'],
  ['Trebuchet MS', '"Trebuchet MS", Helvetica, sans-serif'],
  ['Gill Sans', '"Gill Sans", "Gill Sans MT", Calibri, sans-serif'],
  ['Courier New', '"Courier New", Courier, monospace'],
  ['Lucida Console', '"Lucida Console", Monaco, monospace'],
];

/** How a font value reads: a known one's name, else its first family without quotes ("Adobe Caslon Pro"). */
export function fontName(family: string): string {
  const known = FONT_FAMILIES.find(([, css]) => css === family);
  return known ? known[0] : family.split(',')[0]!.trim().replace(/^["']|["']$/g, '');
}

/** The palette (decision 7.5), written as `rgb()` as the desktop writes colours. */
export const COLORS: readonly (readonly [string, string])[] = [
  ['Black', 'rgb(0, 0, 0)'],
  ['Dark grey', 'rgb(85, 85, 85)'],
  ['Grey', 'rgb(150, 150, 150)'],
  ['White', 'rgb(255, 255, 255)'],
  ['Red', 'rgb(230, 77, 77)'],
  ['Dark red', 'rgb(150, 30, 30)'],
  ['Orange', 'rgb(235, 140, 40)'],
  ['Yellow', 'rgb(255, 255, 0)'],
  ['Gold', 'rgb(190, 145, 0)'],
  ['Green', 'rgb(56, 142, 60)'],
  ['Teal', 'rgb(0, 128, 128)'],
  ['Blue', 'rgb(40, 100, 210)'],
  ['Navy', 'rgb(25, 35, 120)'],
  ['Purple', 'rgb(120, 40, 160)'],
  ['Pink', 'rgb(215, 40, 110)'],
  ['Brown', 'rgb(120, 85, 70)'],
];
